import { useEffect, useRef, useState } from 'react';
import { http } from '../../services/http';
import { getSocket, onInvalidate } from '../../services/socket';

export function useSettingsPoll(isAuthenticated: boolean) {
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maxFileSizeMb, setMaxFileSizeMb] = useState(2048);
  const [organizationName, setOrganizationName] = useState(() => {
    try {
      return localStorage.getItem('al_org_name') || 'АрхивЛинк';
    } catch {
      return 'АрхивЛинк';
    }
  });
  const lastFetchAtRef = useRef(0);

  useEffect(() => {
    const onSettingsUpdated = (e: Event) => {
      const ce = e as CustomEvent<any>;
      const nextName = String(ce?.detail?.organizationName || '').trim();
      if (nextName) {
        setOrganizationName(nextName);
        try { localStorage.setItem('al_org_name', nextName); } catch {}
      }
      if (Object.prototype.hasOwnProperty.call(ce?.detail || {}, 'maintenanceMode')) {
        setMaintenanceMode(!!ce.detail.maintenanceMode);
      }
      if (Object.prototype.hasOwnProperty.call(ce?.detail || {}, 'maxFileSize')) {
        const n = Number(ce.detail.maxFileSize);
        if (Number.isFinite(n) && n > 0) setMaxFileSizeMb(Math.floor(n));
      }
    };
    window.addEventListener('al_settings_updated', onSettingsUpdated as any);
    return () => window.removeEventListener('al_settings_updated', onSettingsUpdated as any);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    let stopped = false;
    let timer: any = null;

    const nextDelayMs = () => {
      const visible = typeof document === 'undefined' ? true : document.visibilityState === 'visible';
      const s = getSocket();
      const connected = !!(s && s.connected);
      const base = connected
        ? (visible ? 1200000 : 3600000)
        : (visible ? 300000 : 600000);
      const jitter = Math.floor(Math.random() * 2000);
      return base + jitter;
    };

    let inflight = false;
    const tick = async (opts?: { force?: boolean }) => {
      if (inflight) return;
      const now = Date.now();
      if (!opts?.force && now - lastFetchAtRef.current < 30_000) return;
      inflight = true;
      try {
        const [settings] = await Promise.all([
          http.get<any>('/settings'),
        ]);
        setMaintenanceMode(!!settings.maintenanceMode);
        setOrganizationName(String(settings.organizationName || 'АрхивЛинк'));
        try { localStorage.setItem('al_org_name', String(settings.organizationName || 'АрхивЛинк')); } catch {}
        const mfs = Number((settings as any)?.maxFileSize);
        if (Number.isFinite(mfs) && mfs > 0) setMaxFileSizeMb(Math.floor(mfs));
        lastFetchAtRef.current = Date.now();
      } catch {}
      finally { inflight = false; }
    };

    const schedule = async () => {
      if (stopped) return;
      await tick();
      if (stopped) return;
      timer = setTimeout(schedule, nextDelayMs());
    };

    void tick({ force: true });
    schedule();

    const onVis = () => {
      if (stopped) return;
      if (timer) clearTimeout(timer);
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        void tick();
      }
      timer = setTimeout(schedule, nextDelayMs());
    };
    try {
      document.addEventListener('visibilitychange', onVis);
    } catch {}

    const off = onInvalidate((e) => {
      if (e.scope === 'settings') {
        void tick({ force: true });
      }
    }, ['settings']);

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      try {
        document.removeEventListener('visibilitychange', onVis);
      } catch {}
      try { off && off(); } catch {}
    };
  }, [isAuthenticated]);

  return { maintenanceMode, organizationName, maxFileSizeMb } as const;
}
